import React, { Component, lazy, createRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isIOS, isChrome } from 'react-device-detect';

import { nbsApiGetConfig } from './store/actions/getConfig';
import { getSessionToken } from './store/actions/session';
import { setCookiesModal } from './store/actions/cookiesModal';
import { getStoredLanguage, setStoredLanguage } from './store/actions/language';
import { setMetaPixels } from './store/actions/metaPixels';
import { gaRoutePageView } from './assets/helpers/metaPixelPageViewHelper';
import config from './constants/config';
import { gtmTrackAllData } from './assets/helpers/GAHelper';
import AppComponentContext from './contexts/AppComponentContext';
import {
  getTwoLetterCountryCode,
  getConsentCookie,
  getMetaPixelParameters,
} from './assets/helpers/utils';
import {
  anyConsent,
  nonRequiredConsent,
  requiredConsentInit,
  requiredConsentAccepted,
  requiredConsentDeclined,
  getCookieConsent,
  declineAllCookies,
  injectSelectedCookies,
  injectAllScripts,
} from './assets/helpers/cookieConsent';

import URLSearchParams from 'url-search-params';
import { QueryClientProvider } from 'react-query';
import queryClient from './assets/helpers/queryClient';
import strings from './assets/localization';
import SuspenseFallback from './hoc/SuspenseFallback/SuspenseFallback';
import MainIndexContainer from './containers/MainIndex/MainIndexContainer';
import paths from './constants/paths';

const TopIndexContainer = lazy(() =>
  import(
    /* webpackChunkName: "top" */ './containers/TopIndex/TopIndexContainer'
  )
);

const CookieConsentContainer = lazy(() =>
  import(
    /* webpackChunkName: "cookie-consent" */ './components/CookieConsent/CookieConsent'
  )
);

const pullToRefreshPrevent = (function () {
  // To determine the direction in which a touch is moving,
  // we hold on to a map from touch identifier to touches
  // from the previous event.
  let previousTouches = {};
  return function (event) {
    // First we get all touches in this event and set up
    // the value which will replace `previousTouches`
    // before this event handler exits.
    const touches = Array.prototype.slice.call(event.touches);
    let nextTouches = {};
    touches.forEach((touch) => {
      nextTouches[touch.identifier] = touch;
    });

    // Pull-to-refresh behavior only happens if we are
    // scrolled to the top of the document, so we can
    // exit early if we are somewhere in the middle.
    if (document.scrollingElement.scrollTop > 0) {
      previousTouches = nextTouches;
      return;
    }

    // Now we know that we are scrolled to the top of
    // the document;
    // look through the current set of touches and see
    // if any of them have moved down the page.
    for (let ix = 0; ix < touches.length; ix++) {
      let touch = touches[ix];
      let id = touch.identifier;
      // If this touch was captured in a previous event
      // and it has moved downwards, we call preventDefault
      // to prevent the pull-to-refresh behavior.
      if (
        id in previousTouches &&
        previousTouches[id].screenY < touch.screenY
      ) {
        event.preventDefault();
        // console.log("event.preventDefault() called")
        break;
      }
    }
    // lastly, we update previousTouches
    previousTouches = nextTouches;
  };
})();

class App extends Component {
  constructor(props) {
    super(props);
    this.scrollEventListener = undefined;

    this.urlLanguage = '';
    this.props.location.pathname;

    this.fbPixelInitialized = createRef();
    this.fbPixelInitialized.current = false;

    this.state = {
      isLandscape: true,
      width: 1024,
      centralWidth: '48%',
      showSideDrawer: null,
      loggedIn: !!this.props.session.token,
      isCookieConsentBannerNeeded: undefined,
      isCookieConsentAccepted: undefined,
      // showCookiesModal: false,
      cookieObj: null,
    };

    // Initializing language logic that doesn't require DOM updates
    const query = new URLSearchParams(this.props.location.search);
    if (query.get('language')) {
      this.urlLanguage = getTwoLetterCountryCode(query.get('language'));
      query.delete('language');

      this.props.history.replace({
        pathname: this.props.history.location.pathname,
        search: query.toString(),
      });
    }
  }

  componentDidMount() {
    this._onWindowResize();
    this.props.getSessionToken();
    this.props.getStoredLanguage();
    window.addEventListener('resize', this._onWindowResize);

    // for ios we will prevent pullToPush only for Chrome
    if (!isIOS || isChrome) {
      document.body.addEventListener('touchmove', pullToRefreshPrevent, {
        passive: false,
      });
      document.body.addEventListener('touchend', pullToRefreshPrevent, {
        passive: false,
      });
    }

    // accept FacebookPixel on first visit requsted by Zoric, remove in case when GDPR is implemented
    nonRequiredConsent(); // facebook pixel

    getMetaPixelParameters().then((metaPixelParams) => {
      // console.log(metaPixelParams);
      this.props.setMetaPixels({
        fbc: metaPixelParams.fbc,
        fbp: metaPixelParams.fbp,
      });
    });

    gaRoutePageView(this.props.history, 'App');
  }

  componentDidUpdate(prevProps, prevState) {
    const { search } = this.props.location;
    const query = new URLSearchParams(search);
    const cookieObj = getCookieConsent();

    if (
      prevProps.getConfig.response.show_cookie_consent !==
      this.props.getConfig.response.show_cookie_consent
    ) {
      const isCookieConsentBannerNeeded =
        this.props.getConfig.response.show_cookie_consent;

      this.setState({
        cookieObj,
        isCookieConsentBannerNeeded,
      });

      // if (!cookieObj && isCookieConsentBannerNeeded) {
      if (cookieObj.initial && isCookieConsentBannerNeeded) {
        // this.setState({ showCookieModal: true });
        this.props.setCookiesModal(true);
      } else if (
        !isCookieConsentBannerNeeded &&
        this.props.getConfig.status !== null
      ) {
        injectAllScripts();
        // nonRequiredConsent(); // facebook pixel
        this.setState({ isCookieConsentAccepted: true });
      } else if (cookieObj && isCookieConsentBannerNeeded) {
        injectSelectedCookies(cookieObj);
        this.setState({ isCookieConsentAccepted: true });
      }

      if (
        this.props.history.location.pathname !== paths.UNSUBSCRIBE &&
        this.props.history.location.pathname !== `${paths.UNSUBSCRIBE}.php` &&
        this.props.history.location.pathname !==
          paths.ANOTHER_CLIENT_CONNECTED &&
        this.props.history.location.pathname !== paths.DEEP_LINK &&
        this.props.history.location.pathname !== `${paths.DEEP_LINK}.php` &&
        this.props.history.location.pathname !== paths.DEEP_LINK_APPSTORE &&
        this.props.history.location.pathname !== paths.NEW_PASSWORD_TOKEN
      ) {
        // this._initializePixels(isCookieConsentBannerNeeded);
        // this is all for facebook pixels
        isCookieConsentBannerNeeded && requiredConsentInit();

        if (
          isCookieConsentBannerNeeded &&
          cookieObj &&
          cookieObj.marketing &&
          cookieObj.marketing.active &&
          cookieObj.marketing.cookiesProviders.facebook.active
        ) {
          requiredConsentAccepted();
        } else if (
          isCookieConsentBannerNeeded &&
          cookieObj &&
          cookieObj.marketing &&
          !cookieObj.marketing.active
        ) {
          requiredConsentDeclined();
        }
      }
    }

    if (this.props.history.location.pathname !== this.props.location.pathname) {
      if (
        cookieObj &&
        cookieObj.analytics &&
        cookieObj.analytics.active &&
        cookieObj.analytics.cookieProviders &&
        cookieObj.analytics.cookieProviders.google &&
        cookieObj.analytics.cookieProviders.google.active
      ) {
        this.props.location.pathname = this.props.history.location.pathname;
        gtmTrackAllData(
          this.props.history.location,
          this.props.getMyProfile.response,
          this.props.getConfig.response
        );
      }
    }

    if (this.props.session.token !== prevProps.session.token) {
      this.setState({ loggedIn: !!this.props.session.token });
      if (query.get('account_confirmation_token')) {
        query.delete('account_confirmation_token');
        this.props.history.replace({
          pathname: this.props.history.location.pathname,
          search: query.toString(),
        });
      }
    }

    if (
      prevState.showSideDrawer === null ||
      prevState.isLandscape !== this.state.isLandscape
    ) {
      this.setState({ showSideDrawer: this.state.isLandscape });
    }

    if (
      !this.scrollEventListener &&
      this.state.showSideDrawer === true &&
      this.state.isLandscape === false
    ) {
      this.freezeBodyScroll(true);
    } else if (this.state.showSideDrawer === false) {
      this.freezeBodyScroll(false);
    }

    if (
      this.state.loggedIn &&
      this.props.getMyProfile.status === 'success' &&
      prevProps.getMyProfile.status !== this.props.getMyProfile.status &&
      this.props.getMyProfile.response &&
      this.props.getMyProfile.response.language &&
      getTwoLetterCountryCode(this.props.getMyProfile.response.language) &&
      getTwoLetterCountryCode(this.props.getMyProfile.response.language) !==
        strings.getLanguage()
    ) {
      // user IS authenticated - set language to the one from getMyProfile API method
      strings.setLanguage(
        getTwoLetterCountryCode(this.props.getMyProfile.response.language)
      );
      this.props.setStoredLanguage(
        getTwoLetterCountryCode(this.props.getMyProfile.response.language)
      );
    } else if (!this.state.loggedIn) {
      // user NOT authenticated
      if (
        this.props.getConfig.status === null &&
        prevProps.getConfig.status === null
      ) {
        if (
          this.urlLanguage.length === 2 &&
          config.SUPPORTED_LANGUAGES.includes(this.urlLanguage) &&
          this.urlLanguage !== strings.getLanguage()
        ) {
          // set language code found in URL
          strings.setLanguage(this.urlLanguage);
          this.getConfig(this.urlLanguage);
          this.props.setStoredLanguage(this.urlLanguage);
          this.urlLanguage = '';
        } else if (
          this.props.language.code &&
          this.props.getMyProfile.status === null &&
          this.props.language.code !== prevProps.language.code &&
          this.props.language.code !== strings.getLanguage()
        ) {
          // set language code from local storage
          strings.setLanguage(this.props.language.code);
          this.getConfig(this.props.language.code);
        } else {
          // just trigger initial get config fetch
          this.props.nbsApiGetConfig({
            ...(this.props.session.token && {
              sess_token: this.props.session.token,
            }),
          });
        }
      }
    }

    if (
      this.props.location.pathname === paths.PRIVACY_POLICY &&
      this.state.isCookieConsentBannerNeeded &&
      // this.state.showCookieModal
      this.props.cookiesModal.showCookiesModal
    ) {
      // console.log('NE PRIKAZUJ MODAL');
      // this.setState({ showCookieModal: false });
      this.props.setCookiesModal(false);
    }

    if (
      this.props.location.pathname === paths.INDEX &&
      this.state.isCookieConsentBannerNeeded &&
      // !this.state.showCookieModal
      !this.props.cookiesModal.showCookiesModal &&
      !this.state.isCookieConsentAccepted
    ) {
      // console.log('E SAD GA PRIKAZI', this.props);
      // this.setState({ showCookiesModal: true });
      this.props.setCookiesModal(true);
    }
  }

  getConfig = (language) => {
    this.props.nbsApiGetConfig({
      language,
      ...(this.props.session.token && { sess_token: this.props.session.token }),
    });
  };

  // _initializePixels = (isCookieConsentBannerNeeded) => {
  //   if (!this.fbPixelInitialized.current) {
  //     anyConsent();
  //     // wait for consent information
  //     if (isCookieConsentBannerNeeded === true) {
  //       requiredConsentInit();
  //       this.fbPixelInitialized.current = true;
  //     } else if (isCookieConsentBannerNeeded === false) {
  //       nonRequiredConsent();
  //       this.fbPixelInitialized.current = true;
  //     }
  //   }
  // };

  _onWindowResize = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    let isLandscape;

    if (width > height && width >= 1024) {
      isLandscape = true;
      this.freezeBodyScroll(false); // remove freeze body scroll if landscape
    } else {
      isLandscape = false;
    }

    config.IS_LANDSCAPE = isLandscape;
    if (isLandscape !== this.state.isLandscape && width !== this.state.width) {
      this.setState({ isLandscape, width });
    } else if (isLandscape !== this.state.isLandscape) {
      this.setState({ isLandscape });
    } else if (width !== this.state.width) {
      this.setState({ width });
    }

    let sideBarWidth = '28%';
    let centralWidth = '44%';
    if (isLandscape) {
      if (width >= 1024 && width < 1280) {
        sideBarWidth = '26%';
        centralWidth = '48%';
      } else if (width >= 1280 && width < 1334) {
        sideBarWidth = '28%';
        centralWidth = '44%';
      } else if (width >= 1334 && width < 1440) {
        sideBarWidth = '26%';
        centralWidth = '48%';
      } else if (width >= 1440) {
        sideBarWidth = '24%';
        centralWidth = '52%';
      }
    }
    this.setState({ centralWidth, sideBarWidth });
  };

  freezeBodyScroll = (e) => {
    if (e) {
      this.scrollEventListener = 'set';
      document.body.addEventListener('touchmove', this.freezeScroll, {
        passive: false,
      });
      document.body.style.overflowY = 'hidden';
    } else {
      this.scrollEventListener = undefined;
      document.body.removeEventListener('touchmove', this.freezeScroll, {
        passive: false,
      });
      document.body.style.overflowY = 'auto';
    }
  };

  freezeScroll = (e) => {
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  sideDrawerToggle = () => {
    if (!this.state.isLandscape) {
      this.setState((prevState) => {
        return { showSideDrawer: !prevState.showSideDrawer };
      });
    }
  };

  sideDrawerClose = () => {
    if (!this.state.isLandscape) {
      this.setState({ showSideDrawer: false });
    }
  };

  _onCookieAccept = () => {
    // this.setState({ showCookiesModal: false, isCookieConsentAccepted: true });
    this.setState({ isCookieConsentAccepted: true });
    this.props.setCookiesModal(false);
    requiredConsentAccepted(); // hack for facebook pixels

    // if (this.props.session.token) {
    //   window.location.href = '/';
    // }
  };

  _onCookieDecline = () => {
    // this.setState({ showCookiesModal: false, isCookieConsentAccepted: true });
    this.setState({ isCookieConsentAccepted: true });
    this.props.setCookiesModal(false);
    requiredConsentDeclined();
    declineAllCookies();

    // if (this.props.session.token) {
    //   window.location.href = '/';
    // }
  };

  render() {
    let layout;
    if (this.props.session.token) {
      // authenticated
      layout = (
        <SuspenseFallback>
          <TopIndexContainer
            {...this.props}
            {...this.state}
            sessionToken={this.props.session.token}
            sideDrawerClose={this.sideDrawerClose}
            sideDrawerToggle={this.sideDrawerToggle}
          />
        </SuspenseFallback>
      );
    } else {
      // not-authenticated
      layout = (
        <MainIndexContainer
          {...this.props}
          isLandscape={this.state.isLandscape}
        />
      );
    }

    return (
      <QueryClientProvider client={queryClient}>
        <AppComponentContext.Provider value={{ ...this.state }}>
          {layout}
        </AppComponentContext.Provider>
        {this.state.isCookieConsentBannerNeeded &&
          this.props.cookiesModal.showCookiesModal && ( // // &&  this.state.showCookieModal
            <SuspenseFallback>
              <CookieConsentContainer
                onAccept={this._onCookieAccept}
                onDecline={this._onCookieDecline}
                history={this.props.history}
                isLandscape={this.state.isLandscape}
                setCookiesModal={this.props.setCookiesModal}
              />
            </SuspenseFallback>
          )}
      </QueryClientProvider>
    );
  }
}

function mapStateToProps(state) {
  return {
    session: state.session,
    getConfig: state.getConfig,
    getMyProfile: state.getMyProfile,
    language: state.language,
    cookiesModal: state.cookiesModal,
  };
}
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      getSessionToken,
      nbsApiGetConfig,
      getStoredLanguage,
      setStoredLanguage,
      setCookiesModal,
      setMetaPixels,
    },
    dispatch
  );
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
